import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Battery, Sun, Check, Star, TrendingUp, Zap, Plus, DollarSign, Loader2 } from 'lucide-react';
import { MobileVerification } from './MobileVerification';
import axios from 'axios';
import { trackInitiateCheckout } from '../utils/fbPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Round to nearest available battery size
const findClosestBatterySize = (target, availableSizes) => {
  if (availableSizes.length === 0) return Math.round(target / 5) * 5;
  return availableSizes.reduce((prev, curr) => 
    Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
  );
};

// Calculate battery recommendations based on inputs and available pricing
const calculateBatteryOptions = (monthlyBill, solarSystemSize, solarYears, solarPrices, batteryPrices) => {
  const annualBill = monthlyBill * 12;
  const hasSolar = solarSystemSize !== 'none';
  
  // Get available sizes from pricing
  const availableSolarSizes = solarPrices.map(p => p.size_kw).sort((a, b) => a - b);
  const availableBatterySizes = batteryPrices.map(p => p.size_kwh).sort((a, b) => a - b);
  
  // Helper to get battery pricing
  const getBatteryPricing = (size) => {
    const pricing = batteryPrices.find(p => p.size_kwh === size);
    if (pricing) {
      return {
        wasPrice: pricing.was_price || pricing.price + 2000,
        nowPrice: pricing.now_price || pricing.price,
      };
    }
    // Fallback
    return {
      wasPrice: size * 900,
      nowPrice: size * 700,
    };
  };
  
  // Helper to get solar pricing
  const getSolarPricing = (size) => {
    const pricing = solarPrices.find(p => p.size_kw === size);
    if (pricing) {
      return {
        wasPrice: pricing.was_price || pricing.price + 2000,
        nowPrice: pricing.now_price || pricing.price,
      };
    }
    return {
      wasPrice: size * 1200,
      nowPrice: size * 900,
    };
  };
  
  // Parse existing solar size for calculations
  let existingSolarKw = 0;
  if (hasSolar) {
    const sizeMap = {
      '3kw': 3,
      '5kw': 5,
      '6.6kw': 6.6,
      '10kw': 10,
      '10kw+': 13,
    };
    existingSolarKw = sizeMap[solarSystemSize] || 5;
  }
  
  // Battery sizing based on monthly bill and solar
  const baseBatterySize = monthlyBill / 35;
  
  // Helper to find closest solar size
  const findClosestSolarSize = (target) => {
    if (availableSolarSizes.length === 0) return Math.round(target);
    return availableSolarSizes.reduce((prev, curr) => 
      Math.abs(curr - target) < Math.abs(prev - target) ? curr : prev
    );
  };
  
  if (hasSolar) {
    // Customer HAS solar - recommend battery only
    // Recommended = 40kWh minimum, Maximum = 50kWh minimum
    const goodSize = findClosestBatterySize(Math.max(10, baseBatterySize * 0.5), availableBatterySizes);
    const bestSize = findClosestBatterySize(Math.max(40, baseBatterySize), availableBatterySizes); // Minimum 40kWh for recommended
    const maxSize = findClosestBatterySize(Math.max(50, baseBatterySize * 1.3), availableBatterySizes); // Minimum 50kWh for max
    
    const goodPricing = getBatteryPricing(goodSize);
    const bestPricing = getBatteryPricing(bestSize);
    const maxPricing = getBatteryPricing(maxSize);
    
    return [
      {
        tier: 'good',
        name: 'Good Savings',
        description: 'Store excess solar for evening use',
        hasSolar: true,
        existingSolarKw,
        batterySizeKwh: goodSize,
        wasPrice: goodPricing.wasPrice,
        nowPrice: goodPricing.nowPrice,
        savingsPercent: 0.25,
        annualSavings: Math.round(annualBill * 0.25),
        recommended: false,
      },
      {
        tier: 'best',
        name: 'Best Value',
        description: 'Optimal storage for your solar system',
        hasSolar: true,
        existingSolarKw,
        batterySizeKwh: bestSize,
        wasPrice: bestPricing.wasPrice,
        nowPrice: bestPricing.nowPrice,
        savingsPercent: 0.40,
        annualSavings: Math.round(annualBill * 0.40),
        recommended: true,
      },
      {
        tier: 'max',
        name: 'Maximum Savings',
        description: 'Maximum independence from the grid',
        hasSolar: true,
        existingSolarKw,
        batterySizeKwh: maxSize,
        wasPrice: maxPricing.wasPrice,
        nowPrice: maxPricing.nowPrice,
        savingsPercent: 0.55,
        annualSavings: Math.round(annualBill * 0.55),
        recommended: false,
      },
    ];
  } else {
    // Customer has NO solar - recommend solar + battery package
    // Recommended = 40kWh minimum, Maximum = 50kWh minimum
    const goodSolar = findClosestSolarSize(Math.max(5, monthlyBill / 60));
    const goodBattery = findClosestBatterySize(Math.max(10, baseBatterySize * 0.5), availableBatterySizes);
    
    const bestSolar = findClosestSolarSize(Math.max(7, monthlyBill / 50));
    const bestBattery = findClosestBatterySize(Math.max(40, baseBatterySize * 0.9), availableBatterySizes); // Minimum 40kWh
    
    const maxSolar = findClosestSolarSize(Math.max(10, monthlyBill / 40));
    const maxBattery = findClosestBatterySize(Math.max(50, baseBatterySize * 1.2), availableBatterySizes); // Minimum 50kWh
    
    // Calculate combined pricing
    const goodSolarPricing = getSolarPricing(goodSolar);
    const goodBatteryPricing = getBatteryPricing(goodBattery);
    
    const bestSolarPricing = getSolarPricing(bestSolar);
    const bestBatteryPricing = getBatteryPricing(bestBattery);
    
    const maxSolarPricing = getSolarPricing(maxSolar);
    const maxBatteryPricing = getBatteryPricing(maxBattery);
    
    return [
      {
        tier: 'good',
        name: 'Good Savings',
        description: 'Entry-level solar + battery package',
        hasSolar: false,
        solarSizeKw: goodSolar,
        batterySizeKwh: goodBattery,
        wasPrice: goodSolarPricing.wasPrice + goodBatteryPricing.wasPrice,
        nowPrice: goodSolarPricing.nowPrice + goodBatteryPricing.nowPrice,
        savingsPercent: 0.55,
        annualSavings: Math.round(annualBill * 0.55),
        recommended: false,
      },
      {
        tier: 'best',
        name: 'Best Value',
        description: 'Optimal solar + battery combination',
        hasSolar: false,
        solarSizeKw: bestSolar,
        batterySizeKwh: bestBattery,
        wasPrice: bestSolarPricing.wasPrice + bestBatteryPricing.wasPrice,
        nowPrice: bestSolarPricing.nowPrice + bestBatteryPricing.nowPrice,
        savingsPercent: 0.70,
        annualSavings: Math.round(annualBill * 0.70),
        recommended: true,
      },
      {
        tier: 'max',
        name: 'Maximum Savings',
        description: 'Large solar + battery for near grid independence',
        hasSolar: false,
        solarSizeKw: maxSolar,
        batterySizeKwh: maxBattery,
        wasPrice: maxSolarPricing.wasPrice + maxBatteryPricing.wasPrice,
        nowPrice: maxSolarPricing.nowPrice + maxBatteryPricing.nowPrice,
        savingsPercent: 0.85,
        annualSavings: Math.round(annualBill * 0.85),
        recommended: false,
      },
    ];
  }
};

export const BatteryResults = ({ monthlyBill, postcode, solarSystemSize, solarYears, enquiryId, onGetQuotes, onBack }) => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [solarPrices, setSolarPrices] = useState([]);
  const [batteryPrices, setBatteryPrices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch pricing data on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/admin/pricing`);
        setSolarPrices(response.data.solar_pricing || []);
        setBatteryPrices(response.data.battery_pricing || []);
      } catch (err) {
        console.error('Error fetching pricing:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPricing();
  }, []);

  const hasSolar = solarSystemSize !== 'none';
  const options = calculateBatteryOptions(monthlyBill, solarSystemSize, solarYears, solarPrices, batteryPrices);
  
  // Get friendly solar size label
  const getSolarSizeLabel = () => {
    const sizeMap = {
      '3kw': '3kW',
      '5kw': '5kW',
      '6.6kw': '6.6kW',
      '10kw': '10kW',
      '10kw+': '10kW+',
    };
    return sizeMap[solarSystemSize] || solarSystemSize;
  };
  
  const handleGetQuotes = (option) => {
    // Track InitiateCheckout in Facebook Pixel
    trackInitiateCheckout('battery', option.tier);
    setSelectedOption(option);
    setShowContactForm(true);
  };

  // Show contact form
  if (showContactForm) {
    return (
      <MobileVerification
        enquiryId={enquiryId}
        selectedOption={selectedOption}
        productType="battery"
        onBack={() => setShowContactForm(false)}
        onVerified={() => {
          if (onGetQuotes) {
            onGetQuotes(selectedOption);
          }
        }}
      />
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-green-100 text-green-700 px-3 py-1.5 rounded-full text-xs font-medium mb-2">
          <Check className="w-3 h-3" />
          {hasSolar 
            ? `Based on your ${getSolarSizeLabel()} solar & $${monthlyBill}/month bill`
            : `Based on your $${monthlyBill}/month electricity bill`
          }
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
          Your Battery {!hasSolar && '+ Solar'} Options
        </h2>
        <p className="text-gray-500 text-sm">
          {hasSolar 
            ? `Add battery storage to maximise your ${getSolarSizeLabel()} system`
            : 'Get solar and battery together for maximum savings'
          }
        </p>
      </div>

      {/* Options Grid */}
      <div className="grid md:grid-cols-3 gap-3">
        {options.map((option) => {
          return (
            <Card 
              key={option.tier}
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl ${
                option.recommended 
                  ? 'border-2 border-cyo-orange shadow-lg scale-[1.01] md:scale-[1.03]' 
                  : 'border border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Recommended Badge */}
              {option.recommended && (
                <div className="absolute top-0 left-0 right-0 bg-cyo-orange text-white text-center py-1 text-xs font-semibold flex items-center justify-center gap-1">
                  <Star className="w-3 h-3 fill-white" />
                  Recommended
                </div>
              )}
              
              <CardContent className={`p-4 ${option.recommended ? 'pt-8' : ''}`}>
                {/* Option Name */}
                <div className="flex items-center gap-2 mb-2">
                  {option.tier === 'good' && <TrendingUp className="w-4 h-4 text-emerald-500" />}
                  {option.tier === 'best' && <Zap className="w-4 h-4 text-cyo-orange" />}
                  {option.tier === 'max' && <Battery className="w-4 h-4 text-blue-500" />}
                  <h3 className={`text-base font-bold ${
                    option.recommended ? 'text-cyo-orange' : 'text-gray-900'
                  }`}>
                    {option.name}
                  </h3>
                </div>
                
                {/* Description */}
                <p className="text-xs text-gray-500 mb-2">
                  {option.description}
                </p>
                
                {/* System Specs */}
                <div className="space-y-2 mb-3">
                  {/* Solar Size - only for no existing solar */}
                  {!option.hasSolar && (
                    <div className="bg-amber-50 rounded-lg p-2">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Sun className="w-3 h-3 text-amber-500" />
                        <p className="text-[10px] text-gray-500 uppercase tracking-wide">Solar System</p>
                      </div>
                      <p className="text-lg font-bold text-gray-900">
                        {option.solarSizeKw} <span className="text-sm font-medium text-gray-500">kW</span>
                      </p>
                    </div>
                  )}
                  
                  {/* Battery Size */}
                  <div className="bg-blue-50 rounded-lg p-2">
                    <div className="flex items-center gap-1 mb-0.5">
                      <Battery className="w-3 h-3 text-blue-500" />
                      <p className="text-[10px] text-gray-500 uppercase tracking-wide">Battery Storage</p>
                    </div>
                    <p className="text-lg font-bold text-gray-900">
                      {option.batterySizeKwh} <span className="text-sm font-medium text-gray-500">kWh</span>
                    </p>
                  </div>
                </div>
                
                {/* Annual Savings */}
                <div className={`rounded-lg p-3 mb-3 ${
                  option.recommended 
                    ? 'bg-orange-50 border border-orange-100' 
                    : 'bg-green-50'
                }`}>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">Expected Annual Savings</p>
                  <p className={`text-2xl font-bold ${
                    option.recommended ? 'text-cyo-orange' : 'text-green-600'
                  }`}>
                    ${option.annualSavings.toLocaleString()}
                    <span className="text-xs font-medium text-gray-500">/year</span>
                  </p>
                  <p className="text-[10px] text-gray-400">
                    ~{Math.round(option.savingsPercent * 100)}% reduction in bills
                  </p>
                </div>
                
                {/* CTA Button */}
                <Button 
                  onClick={() => handleGetQuotes(option)}
                  className={`w-full font-semibold py-4 rounded-lg transition-all ${
                    option.recommended 
                      ? 'bg-cyo-orange hover:bg-cyo-orange-dark text-white shadow-lg hover:shadow-xl' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                  style={option.recommended ? {
                    animation: 'flash-attention 1.5s ease-in-out infinite',
                    backgroundColor: '#F7941D',
                  } : {}}
                >
                  <DollarSign className="w-4 h-4 mr-1" />
                  See My Pricing
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Info Box - Compact */}
      {hasSolar && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Battery className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Why add a battery?</h4>
              <p className="text-xs text-gray-600">
                Store excess solar energy for evening use when electricity costs more.
              </p>
            </div>
          </div>
        </div>
      )}

      {!hasSolar && (
        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3">
          <div className="flex items-start gap-2">
            <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Plus className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 text-sm mb-0.5">Solar + Battery: Complete Package</h4>
              <p className="text-xs text-gray-600">
                Generate power during the day and store it for evening use.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Back button */}
      <div className="text-center">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="text-gray-500 hover:text-gray-700 text-sm py-1"
        >
          ← Start a new comparison
        </Button>
      </div>
    </div>
  );
};
